import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { useToast } from '@pulsepointinc/bi-components';
import { io, type Socket } from 'socket.io-client';
import { EnvironmentMap, getEnvironment } from '../helpers';
import { Environment } from '../helpers/enums';
import { useCurrentWorkspace } from '../hooks/useCurrentWorkspace';
import { ApiConfigs, getApiConfig } from '../lib/api/config';
import { logger } from '../lib/logger';
import { useUserData } from '../modules/GenomeWorkspaceView/LeftView/hooks/useUserData';
import { useAuthStore } from '../stores';
import type { INotification, SocketConnectionProviderData, SocketMessage } from '../types/socket';

export const ROOM_TYPES = {
  BROADCAST: 'broadcast',
  USER: 'user',
  WORKSPACE: 'workspace',
  ACCOUNT: 'account',
  USER_WORKSPACE: 'user_workspace',
  USER_ACCOUNT: 'user_account',
  WORKSPACE_ACCOUNT: 'workspace_account',
  USER_WORKSPACE_ACCOUNT: 'user_workspace_account',
};

const EPHEMERAL_MESSAGE_AUTO_READ_DELAY_MS = 6000;

export const getWebsocketUrl = (): string =>
  process.env.WEBSOCKET_ENV === 'localhost' ? ApiConfigs.local.ws : getApiConfig().ws;

export const SocketConnectionContext = createContext<SocketConnectionProviderData>({
  isConnected: false,
  notifications: [],
});

const serviceName = 'studio';
const environmentKey = getEnvironment(window.location.host);
const environment = environmentKey ? EnvironmentMap[environmentKey] || Environment.PRODUCTION : Environment.PRODUCTION;

export const MultiChannelSocketConnectionContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const processedEventsRef = useRef<Record<string, boolean>>({});
  // Optimized: Subscribe to full user data for multiple fields
  const { userData } = useUserData();
  const { accessToken } = useAuthStore();
  const { showMessage } = useToast();
  const { currentWorkspace } = useCurrentWorkspace();
  const showMessageRef = useRef(showMessage);

  showMessageRef.current = showMessage;

  const markAsRead = useCallback((messageId: string, messageType: string) => {
    if (!socketRef.current) return;
    socketRef.current.emit('mark-as-read', { messageId, type: messageType });
  }, []);

  const handleToast = useCallback(
    (message: SocketMessage) => {
      const content = message.content;
      const { notificationHeader, notificationBody, notificationStatus } = content || {};

      showMessageRef.current(notificationStatus, notificationHeader, notificationBody, {
        onClose: () => {
          markAsRead(message.id, message.type);
        },
        autoClose: content?.ephemeral ? EPHEMERAL_MESSAGE_AUTO_READ_DELAY_MS : false,
        toastId: message.id,
      });
    },
    [markAsRead],
  );

  const showNotification = useCallback(
    (message: SocketMessage) => {
      const content = message.content;
      setNotifications((previous) => [...previous, content]);
      handleToast(message);
    },
    [handleToast],
  );

  const handleNotificationEvent = useCallback(
    (message: SocketMessage) => {
      logger.debug('📨 New notification received:', message);
      logger.debug('📍 Message source:', message?.type || 'unknown');

      const messageContent = message?.content;

      if (!messageContent) {
        logger.debug('⚠️ Message content is undefined, skipping processing.');
        return;
      }

      if (processedEventsRef.current[messageContent.eventID]) {
        return;
      }

      processedEventsRef.current[messageContent.eventID] = true;

      const roomType = message.type;

      logger.debug('🏷️ Room type identified as:', roomType);

      if (roomType) showNotification(message);
    },
    [showNotification],
  );

  const disconnectFromSockets = useCallback(() => {
    if (socketRef.current) {
      logger.debug('🔌 Disconnecting from all rooms...');
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      processedEventsRef.current = {};
    }
  }, []);

  const onConnect = useCallback(() => {
    logger.debug('🔌 Connected to notification service');

    setIsConnected(true);
  }, []);

  const onDisconnect = useCallback(() => {
    logger.debug('❌ Disconnected from notification service');

    setIsConnected(false);
  }, []);

  const onMessageRead = useCallback(({ messageId }: { messageId: string }) => {
    logger.debug('✅ Message marked as read:', messageId);

    setNotifications((prev) =>
      prev.map((msg) => ((msg as INotification).eventID === messageId ? { ...msg, isRead: true } : msg)),
    );
  }, []);

  const onError = useCallback((error: Error) => {
    logger.error('❌ Socket error:', error);
  }, []);

  const connectToSockets = useCallback(() => {
    if (!accessToken) {
      logger.debug('⏭️ Skipping socket connection - no access token');
      return;
    }

    if (!userData.userEmail) {
      logger.debug('⏭️ Skipping socket connection - need at least userId', {
        hasAccessToken: !!accessToken,
        hasUserEmail: !!userData.userEmail,
        hasWorkspaceId: !!currentWorkspace?.workspace_id,
        hasAccountId: !!userData.acctId,
      });
      return;
    }

    if (socketRef.current) {
      logger.debug('🔄 Cleaning up existing socket connection...');
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }

    logger.debug('Connecting to WebSocket with available data:', {
      hasAccessToken: !!accessToken,
      hasUserEmail: !!userData.userEmail,
      hasWorkspaceId: !!currentWorkspace?.workspace_id,
      hasAccountId: !!userData.acctId,
    });

    const url = getWebsocketUrl();

    const socketQuery: Record<string, string> = {
      serviceName,
      environment,
    };

    if (userData.userEmail) {
      socketQuery.userId = userData.userEmail;
    }

    if (currentWorkspace?.workspace_id) {
      socketQuery.workspaceId = currentWorkspace.workspace_id.toString();
    }

    if (userData?.acctId) {
      socketQuery.accountId = userData.acctId.toString();
    }

    const newSocket = io(url, {
      query: socketQuery,
      auth: {
        token: accessToken,
      },
      path:
        process.env.NODE_ENV === 'development' && process.env.WEBSOCKET_ENV === 'localhost'
          ? undefined
          : '/notifications/socket.io',
    });

    socketRef.current = newSocket;

    socketRef.current.on('connect', onConnect);
    socketRef.current.on('disconnect', onDisconnect);
    socketRef.current.on('operation-status', handleNotificationEvent);
    socketRef.current.on('message-read', onMessageRead);
    socketRef.current.on('error', onError);
  }, [
    userData.userEmail,
    accessToken,
    currentWorkspace?.workspace_id,
    userData.acctId,
    onMessageRead,
    handleNotificationEvent,
    onConnect,
    onDisconnect,
    onError,
  ]);

  useEffect(() => {
    connectToSockets();
  }, [connectToSockets]);

  useEffect(() => {
    return () => {
      disconnectFromSockets();
    };
  }, [disconnectFromSockets]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      isConnected,
      notifications,
    }),
    [isConnected, notifications],
  );

  return <SocketConnectionContext.Provider value={contextValue}>{children}</SocketConnectionContext.Provider>;
};

export const useSocketConnection = () => {
  return useContext<SocketConnectionProviderData>(SocketConnectionContext);
};
